import React, { useState } from 'react';
import { forNamespace } from 'app/common/lib/util/i18n';
import { memberId } from '@trello/session-cookie';
import styles from './WorkspacesPreambleCreateTeam.less';
import {
  useWorkspacesPreambleCreateTeamQuery,
  useWorkspacesPreambleAddTeamMembersMutation,
  useWorkspacesPreambleCreateTeamSubmitMutation,
  useWorkspacesPreambleAddBoardToTeamMutation,
} from '@trello/graphql/generated';
import { useAllUsersAreAdminsEnabled } from '@trello/organizations';
import {
  Button,
  ButtonSizes,
  ButtonTypes,
} from 'app/common/components/controls/button/button';
import Alerts from 'app/scripts/views/lib/alerts.coffee';
import { Feature } from 'app/scripts/debug/constants';
import { Spinner } from 'app/src/components/Spinner';
import { TeamTypeSelect } from 'app/src/components/TeamTypeSelect';
import { WorkspacesPreambleBoardMemberList } from './WorkspacesPreambleBoardMemberList';
import { logError } from 'app/scripts/debug/log-error';
import { trackUe, Noun, Verb } from '@trello/analytics';
import { Analytics } from '@trello/atlassian-analytics';
import { SourceType } from '@trello/atlassian-analytics/src/constants/Source';
import { WorkSpacesPreambleTestIds } from '@trello/test-ids';

const format = forNamespace('workspaces preamble');

const noop = () => {};

interface WorkspacesPreambleCreateTeamProps {
  analyticsDirectObj: Noun;
  analyticsSource: SourceType;
  boardId: string;
  onSuccess: (orgName?: string) => void;
}

export const WorkspacesPreambleCreateTeam: React.FunctionComponent<WorkspacesPreambleCreateTeamProps> = ({
  analyticsDirectObj,
  analyticsSource,
  boardId,
  onSuccess,
}) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [teamType, setTeamType] = useState();
  const [isTeamNameValid, setIsTeamNameValid] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);

  const { data } = useWorkspacesPreambleCreateTeamQuery({
    variables: { boardId },
  });

  const allAdminsEnabled = useAllUsersAreAdminsEnabled();

  const [createOrganization] = useWorkspacesPreambleCreateTeamSubmitMutation();

  const [addBoardMembersToTeam] = useWorkspacesPreambleAddTeamMembersMutation();

  const [addBoardToTeam] = useWorkspacesPreambleAddBoardToTeamMutation();

  const onChangeTeamName: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const newTeamName = e?.target?.value;
    if (newTeamName) {
      setTeamName(newTeamName);
      setIsTeamNameValid(newTeamName.trim().length > 0);
    }
  };

  const onCreateTeamSubmit: React.FormEventHandler = async (e) => {
    e.preventDefault();
    setIsSubmitted(true);

    try {
      // 1. Create Team
      const createResponse = await createOrganization({
        variables: {
          type: 'default',
          displayName: teamName.trim(),
          teamType,
          allAdminsEnabled,
        },
      });

      const orgName = createResponse.data?.createOrganization?.name;
      const orgId = createResponse.data?.createOrganization?.id;

      if (orgId && orgName) {
        // 2. Add the current board to the new team.
        await addBoardToTeam({
          variables: { boardId, orgId },
        });

        // 3. Add the chosen members to the team
        const usersToAddToTeam = selectedMembers.map((idMember) => ({
          id: idMember,
        }));
        const membersAddedCount = usersToAddToTeam.length;

        if (membersAddedCount) {
          await addBoardMembersToTeam({
            variables: {
              orgId,
              users: usersToAddToTeam,
            },
          });
        }

        const availableMembersCount = (data?.board?.members || []).filter(
          (member) => memberId !== member.id && member.confirmed,
        ).length;

        Analytics.sendCreatedBoardEvent({
          source: analyticsSource,
          containers: {
            board: { id: boardId },
            organization: { id: orgId },
          },
          attributes: {
            isTemplate: data?.board?.prefs?.isTemplate,
            visibility: data?.board?.prefs?.permissionLevel,
          },
        });
        Analytics.sendTrackEvent({
          action: 'updated',
          actionSubject: 'organization',
          source: analyticsSource,
          containers: {
            board: {
              id: boardId,
            },
            organization: {
              id: orgId,
            },
          },
          attributes: {
            availableMembersCount,
            membersAddedCount,
            newOrg: true,
            updatedOn: 'board',
          },
        });
        trackUe({
          category: Noun.BOARD_VIEW,
          verb: Verb.SUBMITS,
          directObj: analyticsDirectObj,
          context: {
            availableMembersCount,
            boardId,
            membersAddedCount,
            newOrg: true,
            orgId: orgId,
          },
        });

        onSuccess(orgName);
      }
    } catch (err) {
      Alerts.showLiteralText(err.message, 'error', 'alert', 5000);
      logError({
        error: err,
        tags: {
          ownershipArea: 'trello-bizteam',
          feature: Feature.WorkspacesPreamble,
        },
      });
      setIsSubmitted(false);
    }
  };

  const isButtonEnabled = isTeamNameValid && !!teamType && !isSubmitted;

  return (
    <div className={styles.createTeam}>
      <label
        className={styles.createTeamInputLabel}
        htmlFor="workspacesPreambleCreateTeamInput"
      >
        {format(['new-team-input-label'])}
      </label>
      <input
        id="workspacesPreambleCreateTeamInput"
        className={styles.createTeamInput}
        type="text"
        autoComplete="off"
        spellCheck={false}
        onChange={onChangeTeamName}
        autoFocus={true}
        placeholder={format(['new-team-input-placeholder'])}
        value={teamName}
        maxLength={100}
        disabled={isSubmitted}
      />
      <TeamTypeSelect
        onChange={(teamTypeValue) => setTeamType(teamTypeValue)}
        isDisabled={isSubmitted}
        testId={WorkSpacesPreambleTestIds.WorkspacesPreambleSelectTeamType}
      />
      <WorkspacesPreambleBoardMemberList
        boardId={boardId}
        onSelectMember={(currentSelectedMembers) =>
          setSelectedMembers(currentSelectedMembers)
        }
      />
      <Button
        buttonType={
          isButtonEnabled ? ButtonTypes.PRIMARY : ButtonTypes.DISABLED
        }
        className={styles.createTeamButton}
        onClick={isButtonEnabled ? onCreateTeamSubmit : noop}
        size={ButtonSizes.FULLWIDTH}
        testId={WorkSpacesPreambleTestIds.WorkspacesPreambleCreateTeamSubmit}
      >
        {isSubmitted ? (
          <Spinner centered />
        ) : (
          format(['submit-button-create-team'])
        )}
      </Button>
    </div>
  );
};
